import { Link, Route, Routes } from 'react-router-dom';

import { useAuth } from './auth/auth-context';
import { RequireAuth } from './auth/require-auth';
import { SiteFooter } from './components/site-footer';
import { SentimentStrip } from './components/sentiment';
import { SiteHeader } from './components/site-header';
import { FREE_TICKERS } from './lib/brand';
import { formatScore } from './lib/sentiment';
import { AccountPage } from './pages/account';
import { AuthCallbackPage } from './pages/auth-callback';
import { CatalogPage } from './pages/catalog';
import { PrivacyPage, TermsPage } from './pages/legal';
import { ResetConfirmPage } from './pages/reset-confirm';
import { ResetRequestPage } from './pages/reset-request';
import { SignInPage } from './pages/sign-in';
import { SignUpPage } from './pages/sign-up';
import { TickerPage } from './pages/ticker';
import { WatchlistPage } from './pages/watchlist';
import { WatchlistsPage } from './pages/watchlists';
import { usePageTitle } from './lib/page-title';

const foundations = [
  {
    title: 'Evidence first',
    description:
      'Every aggregate will link back to normalized articles, an explicit time window, and a pinned model version.',
  },
  {
    title: 'Work stays off the request path',
    description:
      'FinBERT analysis runs outside the public API. Navigation reads precomputed, versioned results.',
  },
  {
    title: 'Failure is visible',
    description:
      'Stale, partial, empty, and unavailable data are distinct states—not fabricated neutral scores.',
  },
] as const;

/** Illustrative only; labelled as an example on the page, never as live data. */
const SPECIMEN = {
  headline: 'Chipmaker raises full-year revenue guidance on data-center demand',
  positive: 0.91,
  neutral: 0.06,
  negative: 0.03,
} as const;

function Specimen() {
  const score = SPECIMEN.positive - SPECIMEN.negative;
  return (
    <figure className="specimen" aria-labelledby="specimen-caption">
      <figcaption id="specimen-caption" className="specimen-note">
        How one headline is scored (example)
      </figcaption>
      <p className="specimen-headline">{SPECIMEN.headline}</p>
      <SentimentStrip {...SPECIMEN} large />
      <dl className="specimen-probs">
        <div>
          <dt>Positive</dt>
          <dd className="tone-positive">{SPECIMEN.positive.toFixed(2)}</dd>
        </div>
        <div>
          <dt>Neutral</dt>
          <dd>{SPECIMEN.neutral.toFixed(2)}</dd>
        </div>
        <div>
          <dt>Negative</dt>
          <dd className="tone-negative">{SPECIMEN.negative.toFixed(2)}</dd>
        </div>
      </dl>
      <p className="specimen-equation">
        <span>Score = positive − negative</span>
        <span className="specimen-score">{formatScore(score)}</span>
      </p>
    </figure>
  );
}

function HomePage() {
  usePageTitle();
  const { session } = useAuth();

  return (
    <>
      <SiteHeader />
      <main className="page">
        <section className="hero" aria-labelledby="hero-title">
          <div>
            <h1 id="hero-title">How the news feels about your stocks.</h1>
            <p className="lede">
              A fast, explainable view of how recent company news is classified by FinBERT — daily
              sentiment, a 30-day trend, and the scored headlines behind every number.
            </p>
            <div className="hero-actions">
              {session ? (
                <Link className="button" to="/tickers">
                  Browse tickers
                </Link>
              ) : (
                <>
                  <Link className="button" to="/signup">
                    Create a free account
                  </Link>
                  <Link className="button button-quiet" to="/signin">
                    Sign in
                  </Link>
                </>
              )}
            </div>
            <p className="status" role="status">
              <span className="status-dot" aria-hidden="true" />
              Free tier: {FREE_TICKERS.join(' · ')}
            </p>
          </div>
          <Specimen />
        </section>

        <section className="band" aria-labelledby="plans-title">
          <h2 id="plans-title">Start free. Go Pro for the full catalog.</h2>
          <div className="plans">
            <article className="plan">
              <div className="plan-name">
                <h3>Free</h3>
                <span className="plan-badge plan-badge-free">No card needed</span>
              </div>
              <ul className="ticker-chips" aria-label="Free tickers">
                {FREE_TICKERS.map((symbol) => (
                  <li key={symbol}>{symbol}</li>
                ))}
              </ul>
              <p>
                Sentiment overview, 30-day trend, and the latest scored headlines for three of the
                most-watched S&amp;P 500 companies.
              </p>
            </article>
            <article className="plan">
              <div className="plan-name">
                <h3>Pro</h3>
                <span className="plan-badge">Full catalog</span>
              </div>
              <p>
                The expanding S&amp;P 500 catalog with the same evidence-linked view for every
                ticker. Upgrade any time from your account; cancel any time, effective at the end of
                the paid period.
              </p>
            </article>
          </div>
        </section>

        <section className="band" aria-labelledby="principles-title">
          <h2 id="principles-title">Simple boundaries, observable behavior.</h2>
          <dl className="principles">
            {foundations.map((foundation) => (
              <div key={foundation.title}>
                <dt>{foundation.title}</dt>
                <dd>{foundation.description}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function NotFoundPage() {
  usePageTitle('Page not found');
  return (
    <>
      <SiteHeader />
      <main className="centered-page">
        <p className="big-code" aria-hidden="true">
          404
        </p>
        <h1>Page not found</h1>
        <p>This address doesn’t match any page. Check the link, or start again from home.</p>
        <Link className="button" to="/">
          Return home
        </Link>
      </main>
      <SiteFooter />
    </>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/reset" element={<ResetRequestPage />} />
      <Route
        path="/reset/confirm"
        element={
          <RequireAuth>
            <ResetConfirmPage />
          </RequireAuth>
        }
      />
      <Route
        path="/account"
        element={
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        }
      />
      <Route
        path="/tickers"
        element={
          <RequireAuth>
            <CatalogPage />
          </RequireAuth>
        }
      />
      <Route
        path="/tickers/:symbol"
        element={
          <RequireAuth>
            <TickerPage />
          </RequireAuth>
        }
      />
      <Route
        path="/watchlists"
        element={
          <RequireAuth>
            <WatchlistsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/watchlists/:id"
        element={
          <RequireAuth>
            <WatchlistPage />
          </RequireAuth>
        }
      />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
